import type { RefObject } from 'react';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { debounce } from '../lib/debounce';

export const useOnClickOutside = (
  targetRef: RefObject<HTMLElement>,
  callback: () => void,
): void => {
  useEffect(() => {
    const handler = (event: MouseEvent): void => {
      if (targetRef.current && !targetRef.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handler);

    return (): void => {
      document.removeEventListener('mousedown', handler);
    };
  }, [targetRef.current, callback]);
};

export const useOnResize = (
  targetRef: RefObject<HTMLElement> | null,
  callback: () => void,
  isEnabled = false,
): void => {
  const resizeObserver = useRef(null);

  useEffect(() => {
    if (!isEnabled || !targetRef.current || !callback) {
      return;
    }

    const debouncedCallback = debounce(callback, 200);

    const observe = () => {
      resizeObserver.current = new ResizeObserver((entries) => {
        if (entries) {
          debouncedCallback();
        }
      });

      resizeObserver.current.observe(targetRef.current);
    };

    observe();

    return (): void => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [targetRef.current, callback, isEnabled]);
};

export const useOverflowItems = ({
  sectionRef,
  sectionItemsRef,
  moreButtonRef,
  children,
}: {
  sectionRef: RefObject<HTMLElement> | null;
  sectionItemsRef: RefObject<HTMLElement> | null;
  moreButtonRef: RefObject<HTMLElement> | null;
  children: React.ReactNode;
}): {
  visibleItems: React.ReactNode[];
  hiddenItems: React.ReactNode[];
  showMoreButton: boolean;
} => {
  const [visibleItemsIndexes, setVisibleItemsIndexes] = useState<number[]>([]);
  const [shouldRemeasureItems, setShouldRemeasureItems] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  // Trigger measure items for the first time
  useEffect(() => {
    setIsMounted(true);
    setShouldRemeasureItems(true);
  }, []);

  // Re-measure items calculation logic
  useLayoutEffect(() => {
    if (shouldRemeasureItems) {
      // Items (without the More button)
      const itemsElements = Array.from(sectionItemsRef.current.children);

      let stopWidth = 0;
      const visible = [];
      itemsElements.forEach((item: HTMLElement, index) => {
        // Don't count the width of the More button unless it will be visible
        if (visible.length === itemsElements.length - 1) {
          stopWidth -= moreButtonRef.current.offsetWidth;
        }

        // Add the width of the current item and 4px for the indent between items
        // except for the last item
        const isLastItem = index === itemsElements.length - 1;
        const itemWidth = item.offsetWidth + (isLastItem ? 0 : 4);

        stopWidth += itemWidth;
        if (sectionItemsRef.current.offsetWidth >= stopWidth) {
          visible.push(index);
        }
      });

      setVisibleItemsIndexes(visible);
      setShouldRemeasureItems(false);
    }
  }, [shouldRemeasureItems]);

  // Re-measure items when children change
  useEffect(() => {
    setShouldRemeasureItems(true);
  }, [children]);

  // Re-measure items when center section width changes
  const shouldHandleResize = useMemo(() => React.Children.toArray(children).length > 0, [children]);
  const handleResize = useCallback(() => {
    setShouldRemeasureItems(true);
  }, []);
  useOnResize(sectionRef, handleResize, shouldHandleResize);

  // Split center section items into visible and hidden
  const [visibleItems, hiddenItems] = useMemo(() => {
    const allItems =
      React.Children.map(
        children,
        (child, index) => child && React.cloneElement(child as React.ReactElement, { key: index }),
      ) || [];

    if (!isMounted || shouldRemeasureItems) {
      return [allItems, []];
    } else {
      const visible = allItems.filter((_, index) => visibleItemsIndexes.includes(index));
      const hidden = allItems.filter((_, index) => !visibleItemsIndexes.includes(index));

      return [visible, hidden];
    }
  }, [shouldRemeasureItems, children, visibleItemsIndexes, isMounted]);

  const showMoreButton = useMemo(
    () => shouldRemeasureItems || hiddenItems.length > 0,
    [shouldRemeasureItems, hiddenItems],
  );

  return { visibleItems, hiddenItems, showMoreButton };
};
